import React from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { 
  Sparkles, 
  Gamepad2, 
  Edit3, 
  History, 
  Share2, 
  DollarSign,
  Zap,
  Code,
  Image as ImageIcon,
  Play
} from 'lucide-react';

const Help = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'what-is-gameos',
      title: 'What is GameOS?',
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            <strong>GameOS.dev</strong> is the world's first game vibe coding agent, letting teams of any size build games that earn revenue from Day One.
          </p>
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-white mb-3">What GameOS can do:</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <Zap className="text-purple-400 flex-shrink-0 mt-1" size={16} />
                <span><strong>Prompt to full game:</strong> Just say what you want — GameOS builds your game instantly.</span>
              </li>
              <li className="flex items-start gap-2">
                <DollarSign className="text-green-400 flex-shrink-0 mt-1" size={16} />
                <span><strong>Built-in monetization:</strong> Earn revenue from day one with our 90/10 revenue share.</span>
              </li>
              <li className="flex items-start gap-2">
                <ImageIcon className="text-blue-400 flex-shrink-0 mt-1" size={16} />
                <span><strong>Asset generation:</strong> Generate characters, sprites, and backgrounds with AI.</span>
              </li>
              <li className="flex items-start gap-2">
                <Code className="text-pink-400 flex-shrink-0 mt-1" size={16} />
                <span><strong>Conversational game builder:</strong> Chat with an AI agent to iteratively build and refine playable HTML5 games.</span>
              </li>
              <li className="flex items-start gap-2">
                <Play className="text-emerald-400 flex-shrink-0 mt-1" size={16} />
                <span><strong>Shareable play links:</strong> Every game can be shared via a public <code className="bg-black/40 px-1 rounded text-xs">/play/&lt;slug&gt;/&lt;id&gt;</code> URL that works without logging in.</span>
              </li>
              <li className="flex items-start gap-2">
                <Share2 className="text-pink-400 flex-shrink-0 mt-1" size={16} />
                <span><strong>Instant sharing:</strong> Preview and share your game instantly via live links.</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Gamepad2,
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            Getting started with GameOS is easy— no complicated setups:
          </p>
          <div className="space-y-4">
            <div className="bg-gray-900/50 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
                <h4 className="font-semibold text-white">Sign Up</h4>
              </div>
              <p className="text-gray-400 ml-11">Go to GameOS.dev and sign up — it takes less than a minute.</p>
            </div>
            
            <div className="bg-gray-900/50 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                <h4 className="font-semibold text-white">Describe Your Game</h4>
              </div>
              <p className="text-gray-400 ml-11">Simply type what you want to build. For example: "Create a space shooter game with pixel art graphics"</p>
              <div className="ml-11 mt-3 bg-black/50 rounded p-3 border border-purple-500/30">
                <p className="text-sm text-purple-300 italic">
                  Example: "Build a puzzle game set in an ancient temple, with hidden traps, keys to unlock doors, and a final boss guarding a treasure."
                </p>
              </div>
            </div>
            
            <div className="bg-gray-900/50 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
                <h4 className="font-semibold text-white">Play Your Game</h4>
              </div>
              <p className="text-gray-400 ml-11">In minutes, you'll have a complete, playable game that matches your vision.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'creating-games',
      title: 'Creating Your First Game',
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            GameOS processes your description step by step — generating the game mechanics, characters, assets, and code automatically. You can work in a conversational loop with the AI to tweak visuals, controls, difficulty, and more until it feels right.
          </p>
          <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-lg p-6 border border-purple-500/30">
            <h4 className="text-lg font-semibold text-white mb-3">Example Prompts:</h4>
            <div className="space-y-3">
              <div className="bg-black/30 rounded p-3">
                <p className="text-purple-300">"Create a side-scrolling platformer with a ninja character jumping between buildings"</p>
              </div>
              <div className="bg-black/30 rounded p-3">
                <p className="text-purple-300">"Build a snake game but make it cyberpunk themed with neon graphics"</p>
              </div>
              <div className="bg-black/30 rounded p-3">
                <p className="text-purple-300">"Make a tower defense game set in medieval times with knights and archers"</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-white mb-3">Generation Process:</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-gray-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Game concept & mechanics (40%)</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Game code generation (60%)</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Asset generation (80%)</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span>Final compilation (100%)</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'viewing-games',
      title: 'Managing Your Games',
      icon: Gamepad2,
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            All your created games are accessible from your Dashboard. Here you can:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3 text-gray-300">
              <Play className="text-purple-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <strong className="text-white">View & Play Games:</strong> Click on any game card to open and play it in full screen
              </div>
            </li>
            <li className="flex items-start gap-3 text-gray-300">
              <Code className="text-blue-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <strong className="text-white">Export Code:</strong> Pro users can export the complete game code
              </div>
            </li>
            <li className="flex items-start gap-3 text-gray-300">
              <Share2 className="text-green-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <strong className="text-white">Share Games:</strong> Get instant shareable links to your games
              </div>
            </li>
          </ul>
        </div>
      )
    },
    {
      id: 'monetization',
      title: 'Monetization & Revenue Share',
      icon: DollarSign,
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-lg p-6 border border-green-500/30">
            <h4 className="text-2xl font-bold text-white mb-4">Fair Revenue Sharing</h4>
            <p className="text-gray-300 mb-6">
              Unlike other platforms, we believe in fair compensation for creators.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-6xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  90%
                </div>
                <div className="text-xl text-white font-semibold mb-2">You Keep</div>
                <p className="text-gray-400">All ad revenue from your games goes directly to you</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold text-gray-400 mb-2">10%</div>
                <div className="text-xl text-white font-semibold mb-2">Platform Fee</div>
                <p className="text-gray-400">Fair platform fee to keep GameOS running and improving</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-900/50 rounded-lg p-4 border border-white/10">
            <h4 className="font-semibold text-white mb-2">How to Enable Monetization:</h4>
            <p className="text-gray-400 mb-2">You can turn ads on or off for each game from the Game Detail page.</p>
            <ul className="list-disc list-inside text-gray-400 space-y-1 text-sm">
              <li>Open your Dashboard and click on a game card.</li>
              <li>Use the <span className="text-green-400 font-semibold">Monetization</span> section to enable ads.</li>
              <li>When ads are enabled, shared /play links will show ads to your players.</li>
              <li>You keep <span className="text-green-400 font-semibold">90%</span> of all ad revenue. GameOS keeps 10% as a platform fee.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'pricing',
      title: 'Pricing & Plans',
      icon: DollarSign,
      content: (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-6 border border-white/10">
              <h4 className="text-xl font-bold text-white mb-2">Free Tier</h4>
              <div className="text-3xl font-bold text-white mb-4">$0<span className="text-lg text-gray-400">/month</span></div>
              <ul className="space-y-2 text-gray-300">
                <li>✓ Create 1 game for free</li>
                <li>✓ 100 credits for editing</li>
                <li>✓ Basic ad integration</li>
                <li>✓ 90% revenue share</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-lg p-6 border-2 border-purple-500">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm font-bold inline-block mb-3">
                MOST POPULAR
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Pro Tier</h4>
              <div className="text-3xl font-bold text-white mb-4">$19.99<span className="text-lg text-gray-300">/month</span></div>
              <ul className="space-y-2 text-white">
                <li>✓ Unlimited game generation</li>
                <li>✓ 2000 credits/month</li>
                <li>✓ AI image generation</li>
                <li>✓ Asset generation</li>
                <li>✓ Export game code</li>
                <li>✓ Priority support</li>
              </ul>
              <Button 
                className="w-full mt-4 bg-white text-purple-900 hover:bg-gray-200"
                onClick={() => navigate('/pricing')}
              >
                Upgrade Now
              </Button>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'faq',
      title: 'Frequently Asked Questions',
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <div className="space-y-4">
            {[
              {
                q: "How do I start building a game in GameOS?",
                a: "Simply sign up on GameOS.dev, open the Create page, and type your game idea in natural language. GameOS will generate the first playable version and then you can refine it through chat."
              },
              {
                q: "What types of games can GameOS create?",
                a: "GameOS can generate various genres, including puzzle, RPG, adventure, strategy, platformers, and retro pixel games — all based on your description."
              },
              {
                q: "Can I share my game with others?",
                a: "Absolutely! Every game can be shared instantly through your dashboard. Just click the Share button on a game to get a public /play link that anyone can open, even without an account."
              },
              {
                q: "Does GameOS require coding skills?",
                a: "No coding required. Just describe your game in natural language, and GameOS handles the rest."
              },
              {
                q: "How does monetization work with GameOS?",
                a: "When you enable ads on a game, shared players see ads alongside gameplay. We track ad impressions and split revenue 90/10 — you keep 90% of all ad revenue generated by your games, while we take a 10% platform fee."
              },
              {
                q: "Can I export my game code?",
                a: "Yes! Pro tier users can export the complete game code and host it anywhere they want."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-900/50 rounded-lg p-4 border border-white/10">
                <h5 className="font-semibold text-white mb-2">{faq.q}</h5>
                <p className="text-gray-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Seo
        title="Help & Documentation – GameOS"
        description="Learn how to build, refine, share and monetize AI-generated games on GameOS: prompting guides, credits, monetization, remixing and troubleshooting."
        path="/help"
      />
      {/* Header */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">GO</span>
              </div>
              <span className="text-xl font-bold text-white">GameOS</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">
            GameOS [BETA] <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Help Center</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            A comprehensive guide to help you build, manage, and monetize your games with GameOS
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-2xl p-8 border border-purple-500/30 mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Quick Navigation</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-black/30 hover:bg-black/50 transition-colors border border-white/10 hover:border-purple-500/50"
                >
                  <Icon className="text-purple-400" size={20} />
                  <span className="text-gray-300 hover:text-white">{section.title}</span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-12">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <div 
                key={section.id} 
                id={section.id}
                className="scroll-mt-24"
              >
                <div className="bg-gray-900/50 rounded-2xl p-8 border border-white/10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <Icon className="text-purple-400" size={28} />
                    </div>
                    <h2 className="text-3xl font-bold text-white">{section.title}</h2>
                  </div>
                  {section.content}
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Support */}
        <div className="mt-12 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-2xl p-8 border border-purple-500/30 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Still Need Help?</h3>
          <p className="text-gray-300 mb-6">
            Join our Telegram community for support and to connect with other game creators
          </p>
          <Button 
            size="lg"
            onClick={() => window.open('https://t.me/tokenos_ai', '_blank')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            Join Telegram Support
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="flex justify-center gap-4 mb-4">
              <a 
                href="https://x.com/GameOS_Dev" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow us on X"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a 
                href="https://t.me/tokenos_ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Join our Telegram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </a>
            </div>
            <p className="text-gray-400">© 2025 GameOS - Powered by <a href="https://tokenos.ai" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">TokenOS</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
